import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Pause, Play, Send, Trash2 } from 'lucide-react'
import { api } from '@/api/client'
import { useCreatorMutation, useCreatorPool } from '@/lib/creators'
import { Button, ButtonLink, ConfirmDialog, ErrorState, PageHeader, PlatformMark, Skeleton, StatusPill, errorText, useQueryParam, useToast, useWidePage } from '@/ui'
import { Members } from './Members'
import { PersonDrawer } from './PersonDrawer'
import { CriteriaCard, DiscoveryCard } from './PoolCards'
import { PoolForm } from './PoolForm'

/** /creators/:id: one pool. Finding people, the criteria, and who is in it. */
export default function PoolPage() {
  useWidePage()
  const { id } = useParams()
  const poolId = Number(id)
  const q = useCreatorPool(poolId)
  const toast = useToast()
  const navigate = useNavigate()
  const [person, setPerson] = useQueryParam('person')
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const pool = q.data

  const setStatus = useCreatorMutation((status: 'active' | 'paused') => api.updateCreatorPool(poolId, { status }))
  const del = useCreatorMutation(() => api.deleteCreatorPool(poolId))
  const parent = { to: '/creators', label: '타깃 풀' }

  if (q.error && !pool) {
    return (
      <>
        <PageHeader title="풀" parent={parent} />
        <ErrorState title="풀을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      </>
    )
  }
  if (!pool) {
    return (
      <>
        <PageHeader title="풀" parent={parent} />
        <Skeleton className="h-48" />
      </>
    )
  }

  const toggle = async () => {
    const next = pool.status === 'active' ? 'paused' : 'active'
    try {
      await setStatus.mutateAsync(next)
      toast.success(next === 'paused' ? '멈췄다' : '다시 돌린다')
    } catch (e) {
      toast.error(`바꾸지 못했다: ${errorText(e)}`)
    }
  }

  return (
    <>
      <PageHeader
        parent={parent}
        docTitle={pool.name}
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            <PlatformMark platform={pool.platform} />
            {pool.name}
            <StatusPill domain="app" status={pool.status} className="align-middle" />
          </span>
        }
        description={`${pool.members_in.toLocaleString('ko-KR')}명이 들어 있다.`}
        actions={
          <>
            <ButtonLink variant="primary" icon={Send} to={`/creators/outreaches/new?pool=${pool.id}`}>
              이 풀로 DM 보내기
            </ButtonLink>
            <Button icon={pool.status === 'active' ? Pause : Play} loading={setStatus.isPending} onClick={() => void toggle()}>
              {pool.status === 'active' ? '멈춤' : '재개'}
            </Button>
            <Button variant="dangerOutline" icon={Trash2} onClick={() => setDeleting(true)}>
              삭제
            </Button>
          </>
        }
      />
      {pool.status === 'paused' && (
        <p role="status" className="relative mb-5 overflow-hidden rounded-lg border border-border bg-card py-3 pl-5 pr-4 text-table">
          <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-status-publishing" />
          멈춘 풀이다. 매일 자동으로 찾기는 쉬고, 지금 찾기는 누르면 돈다.
        </p>
      )}

      <div className="space-y-5">
        <div className="grid items-start gap-5 lg:grid-cols-2">
          <DiscoveryCard pool={pool} />
          <CriteriaCard pool={pool} onEdit={() => setEditing(true)} />
        </div>
        <Members pool={pool} onOpen={(cid) => setPerson(String(cid))} />
      </div>

      {Number(person) > 0 && <PersonDrawer id={Number(person)} poolId={pool.id} onClose={() => setPerson('')} />}
      {editing && (
        <PoolForm
          pool={pool}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            toast.success('조건을 저장했다')
          }}
        />
      )}
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title="풀을 지울까"
        description="풀과 그 안의 명단만 지운다. 찾아 둔 사람의 정보는 다른 풀에서 그대로 쓴다."
        confirmLabel="지우기"
        confirmVariant="destructive"
        loading={del.isPending}
        onConfirm={async () => {
          try {
            await del.mutateAsync()
            toast.success(`${pool.name} 풀을 지웠다`)
            navigate('/creators')
          } catch (e) {
            toast.error(`지우지 못했다: ${errorText(e)}`)
          }
        }}
      />
    </>
  )
}
